/**
 * Google Sign-In Button
 *
 * Uses the Google Identity Services (GIS) library to render the
 * "Sign in with Google" button and handle the credential response.
 *
 * The GIS script is loaded lazily on first render.
 *
 * Env: VITE_GOOGLE_CLIENT_ID must be set.
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { Box, Alert, Divider, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS, DASHBOARD_HOME } from '@yourownmetric/shared'

import { needsMfa, useGoogleLoginMutation } from '@/apis/authApi'
import { useAppDispatch } from '@/app/hooks'

import { setCredentials } from './authSlice'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

// Google Identity Services types
interface GoogleCredentialResponse {
    credential: string
    select_by: string
}

interface GoogleAccounts {
    id: {
        initialize: (config: {
            client_id: string
            callback: (response: GoogleCredentialResponse) => void
            auto_select?: boolean
        }) => void
        renderButton: (
            parent: HTMLElement,
            options: {
                theme?: 'outline' | 'filled_blue' | 'filled_black'
                size?: 'large' | 'medium' | 'small'
                text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
                shape?: 'rectangular' | 'pill' | 'circle' | 'square'
                width?: number
            },
        ) => void
    }
}

declare global {
    interface Window {
        google?: { accounts: GoogleAccounts }
    }
}

/** Load the GIS script once */
function loadGISScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.google?.accounts) {
            resolve()
            return
        }

        const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]')
        if (existing) {
            existing.addEventListener('load', () => resolve())
            return
        }

        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
        document.head.appendChild(script)
    })
}

interface GoogleSignInButtonProps {
    /** Button text variant */
    text?: 'signin_with' | 'signup_with' | 'continue_with'
    /** Where to go after signing in (default: the dashboard home). */
    next?: string
    /** MFA accounts: show the code step (without it, the user is sent to /login). */
    onMfaRequired?: (challengeToken: string) => void
}

export function GoogleSignInButton({
    text = 'continue_with',
    next = DASHBOARD_HOME,
    onMfaRequired,
}: GoogleSignInButtonProps) {
    const buttonRef = useRef<HTMLDivElement>(null)
    const [googleLogin, { error: googleError }] = useGoogleLoginMutation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [loadError, setLoadError] = useState<string | null>(null)

    const handleCredentialResponse = useCallback(
        async (response: GoogleCredentialResponse) => {
            try {
                const result = await googleLogin({ idToken: response.credential }).unwrap()
                if (needsMfa(result)) {
                    if (onMfaRequired) {
                        onMfaRequired(result.mfaChallengeToken)
                    } else {
                        navigate(DASHBOARD_AUTH_PATHS.login)
                    }
                    return
                }
                dispatch(setCredentials(result))
                navigate(next)
            } catch {
                // Error handled by RTK Query
            }
        },
        [googleLogin, dispatch, navigate, next, onMfaRequired],
    )

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) {
            return
        }

        let cancelled = false

        loadGISScript()
            .then(() => {
                if (cancelled || !buttonRef.current || !window.google) {
                    return
                }

                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleCredentialResponse,
                })

                window.google.accounts.id.renderButton(buttonRef.current, {
                    theme: 'outline',
                    size: 'large',
                    text,
                    shape: 'rectangular',
                    width: 400,
                })
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError('Failed to load Google Sign-In')
                }
            })

        return () => {
            cancelled = true
        }
    }, [handleCredentialResponse, text])

    // Don't render anything if Google Client ID is not configured
    if (!GOOGLE_CLIENT_ID) {
        return null
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    OR
                </Typography>
            </Divider>

            {(googleError || loadError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loadError ?? 'Google sign-in failed. Please try again.'}
                </Alert>
            )}

            <Box
                ref={buttonRef}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: 44,
                }}
            />
        </Box>
    )
}
